import { utils } from 'frontend-js-shared'

import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

/**
 * Pure helpers for checklist visibility transitions: which items just became
 * hidden, applying the answer reset, and which answers a trigger change
 * would lose. Ids are normalized through utils.getEntityId, matching the
 * maps produced by utils.checklists.computeChecklistVisibility. The
 * answer-data predicate and the visibility-transition diff pair live in
 * frontend-js-shared (v1.6.0) so web and mobile share one implementation;
 * the answer reset and the dependents walk stay web-local for now.
 */

export const findNewlyHiddenItemIds = utils.checklists.findNewlyHiddenItemIds
export const hasItemData = utils.checklists.hasItemData

/** New items array with the answers cleared for every hidden id; separators
 * pass through untouched. Shape comes from utils.checklists.clearItemAnswers. */
export function resetNewlyHiddenAnswers(items, newlyHiddenIds) {
  const hiddenIds = new Set(newlyHiddenIds)

  return items.map(item => {
    if (item?.type === ITEM_TYPES.SEPARATOR) return item

    return hiddenIds.has(utils.getEntityId(item))
      ? utils.checklists.clearItemAnswers(item)
      : item
  })
}

/** Currently visible dependents carrying answers that would become hidden
 * when triggerItem's response changes to nextResponse — the set a data-loss
 * confirmation warns about. A dependent with several rules only leaves the
 * set when none of them is met in the hypothetical state: the changed
 * trigger's rule reads nextResponse while every other rule reads its own
 * trigger's current response. */
export function getDependentsToReset({
  items,
  triggerItem,
  nextResponse,
  dependentsByTriggerId,
  visibilityById,
}) {
  const dependents =
    dependentsByTriggerId.get(utils.getEntityId(triggerItem)) ?? []

  if (dependents.length === 0) {
    return []
  }

  const nextTrigger = { ...triggerItem, response: nextResponse }
  const itemsById = new Map(
    items.map(item => [utils.getEntityId(item), item])
  )
  const dependentsToReset = []

  dependents.forEach(dependent => {
    if (visibilityById.get(utils.getEntityId(dependent)) !== true) return

    if (
      isDependentVisibleAfterChange({
        dependent,
        changedTriggerId: utils.getEntityId(triggerItem),
        nextTrigger,
        itemsById,
      })
    ) {
      return
    }

    if (dependent?.type === ITEM_TYPES.SEPARATOR) {
      dependentsToReset.push(
        ...getAnsweredSectionChildren(items, dependent, visibilityById)
      )
      return
    }

    if (utils.checklists.hasItemData(dependent))
      dependentsToReset.push(dependent)
  })

  return dependentsToReset
}

function isDependentVisibleAfterChange({
  dependent,
  changedTriggerId,
  nextTrigger,
  itemsById,
}) {
  return (dependent?.visibilityRules ?? []).some(rule => {
    const ruleTriggerId = utils.getEntityId(rule?.triggerItemId)
    const ruleTrigger =
      ruleTriggerId === changedTriggerId
        ? nextTrigger
        : itemsById.get(ruleTriggerId)

    // A rule whose trigger cannot be resolved keeps the dependent visible
    // (corrupt data is shown, never hidden).
    if (!ruleTrigger) return true

    return utils.checklists.isVisibilityRuleMet({
      visibilityRule: rule,
      triggerQuestion: ruleTrigger,
    })
  })
}

function getAnsweredSectionChildren(items, separator, visibilityById) {
  const separatorId = utils.getEntityId(separator)

  return getSectionChildren(items, separatorId).filter(
    child =>
      visibilityById.get(utils.getEntityId(child)) === true &&
      utils.checklists.hasItemData(child)
  )
}

/** Flat items between a section separator and the next one. */
function getSectionChildren(items, separatorId) {
  const separatorIndex = items.findIndex(
    item => utils.getEntityId(item) === separatorId
  )
  if (separatorIndex === -1) return []

  const nextSeparatorIndex = items.findIndex(
    (item, index) =>
      index > separatorIndex && item?.type === ITEM_TYPES.SEPARATOR
  )

  return items.slice(
    separatorIndex + 1,
    nextSeparatorIndex === -1 ? items.length : nextSeparatorIndex
  )
}
